import React, { useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import {
  AddTrailerForm,
  IAddTrailerFormProps,
} from 'apps/marketplace/pages/shipment-details/components/ShipmentProgress/components/StopBasicInfo/AddTrailer/AddTrailerForm'
import { TAddTrailerSchema } from 'apps/marketplace/pages/shipment-details/components/ShipmentProgress/components/StopBasicInfo/AddTrailer/AddTrailerSchema'
import { useCarrierApplicationInfoQuery } from 'apps/marketplace/pages/shipment-details/components/ShipmentProgress/components/StopBasicInfo/AddTrailer/graphql/CarrierApplicationInfo'
import {
  ICreateTrailerMutation,
  ICreateTrailerMutationVariables,
  useCreateTrailerMutation,
} from 'apps/marketplace/pages/shipment-details/components/ShipmentProgress/components/StopBasicInfo/AddTrailer/graphql/CreateTrailerMutation'
import { useAddTrailerForm } from 'apps/marketplace/pages/shipment-details/components/ShipmentProgress/components/StopBasicInfo/AddTrailer/useAddTrailerForm'

export interface IAddTrailerProps extends Omit<IModalBaseProps, 'modalType'> {
  shipperApplicationId?: string
  carrierApplicationId?: string
  onSuccess?: (
    response: ICreateTrailerMutation['createTrailer'],
    mutationVariables: ICreateTrailerMutationVariables['input']
  ) => void
  AddTrailerFormProps: Partial<IAddTrailerFormProps>
  defaultValues: Partial<TAddTrailerSchema>
}

/**
 * Modal that handles adding a trailer to a shipment on the 'Missing Trailers' part of the trailer management page.
 */
export const AddTrailer = ({
  carrierApplicationId,
  onSuccess = () => {},
  onClose,
  open,
  shipperApplicationId,
  defaultValues,
  AddTrailerFormProps,
  ...props
}: IAddTrailerProps) => {
  const methods = useAddTrailerForm({
    defaultValues: {
      carrier: '',
      shipment: '',
      status: '',
      trailerNumber: '',
      trailerNotes: '',
      ...defaultValues,
    },
  })

  const [createTrailer] = useCreateTrailerMutation()

  const { handleSubmit } = methods || {}

  const [mutationErrors, setMutationErrors] = useState<string | null>(null)

  const { data } = useCarrierApplicationInfoQuery({
    variables: {
      carrierId: carrierApplicationId || '',
    },
    skip: !carrierApplicationId,
  })
  const { carrierApplication: carrierApplicationProp } = data || {}

  const { openToast } = useGlobalToast()

  const saveHandler = async (formData: TAddTrailerSchema) => {
    const {
      arrivedAt,
      carrier: carrierId,
      emptiedAt,
      facility: facilityUuid,
      shipment: shipmentId,
      trailerNotes: note,
      trailerNumber,
    } = formData || {}
    const shipperFacilityUuid = facilityUuid || null
    const facilityArrivedAtDate = arrivedAt.date
    const facilityArrivedAtTime = arrivedAt.time === '' ? null : arrivedAt.time
    const emptiedAtDate = emptiedAt.date
    const emptiedAtTime = emptiedAt.time === '' ? null : emptiedAt.time

    setMutationErrors(null)

    try {
      const inputVariables = {
        carrierId,
        number: trailerNumber,
        shipmentId,
        note,
        shipperFacilityUuid,
        facilityArrivedAtDate,
        facilityArrivedAtTime,
        emptiedAtDate,
        emptiedAtTime,
      }
      const { data, errors } = await createTrailer({
        variables: {
          input: inputVariables,
        },
        refetchQueries: ['MissingTrailers'],
      })
      const { createTrailer: response } = data || {}
      const { errors: responseErrors } = response || {}
      if (errors && errors.length > 0) {
        setMutationErrors(errors.map(e => e.message).join('; '))
      } else if (responseErrors && responseErrors.length > 0) {
        setMutationErrors(responseErrors.map(e => e.message).join('; '))
      } else {
        onSuccess(response, inputVariables)
        openToast({
          toastType: 'default',
          toastMessage: 'Trailer successfully added.',
        })
        onClose()
      }
    } catch (e) {
      setMutationErrors('There was an error adding this trailer')
    }
  }

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: () => {
        onClose()
      },
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      action: handleSubmit(saveHandler),
      type: 'confirm',
      dataTest: 'confirm-button',
    },
  ]

  return (
    <ModalBase
      dataTest='AddTrailer'
      modalType={MODAL_TYPES.form}
      title='Add Trailer'
      size={4}
      open={open}
      actions={actions}
      headerClassName='pb0'
      onClose={onClose}
      {...props}>
      <FormProvider {...methods}>
        <AddTrailerForm
          {...AddTrailerFormProps}
          mutationErrors={mutationErrors}
          shipperApplicationId={shipperApplicationId}
          carrierApplication={carrierApplicationProp || undefined}
        />
      </FormProvider>
    </ModalBase>
  )
}
